/**
 * Organization Settings - Usage Section
 * Displays usage statistics and analytics
 */

import React, { useEffect, useState } from 'react';
import { Users, Play, FileText, Layers, Mic, Clock, TrendingUp, AlertCircle } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { apiGet } from '../../../utils/api';

interface UsageData {
    simulations_run: number;
    active_sessions: number;
    candidates_evaluated: number;
    custom_simulations: number;
    voice_mode_simulations: number;
    total_voice_seconds: number;
    total_voice_minutes: number;
}

interface AllTimeData {
    total_simulations: number;
    completed_simulations: number;
    unique_users: number;
    avg_session_duration_minutes: number;
    total_duration_minutes: number;
}

interface VoiceModeData {
    total_voice_simulations: number;
    total_voice_seconds: number;
    total_voice_minutes: number;
    voice_usage_percentage: number;
}

interface UsageResponse {
    status: string;
    data: {
        org_id: string;
        usage: UsageData;
        all_time: AllTimeData;
        voice_mode: VoiceModeData;
        custom_simulations: {
            total: number;
            active: number;
        };
        limits: {
            max_members: number;
        };
    };
}

export function OrganizationSettingsUsage() {
    const { currentOrg } = useAuth();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [usageData, setUsageData] = useState<UsageData | null>(null);
    const [allTimeData, setAllTimeData] = useState<AllTimeData | null>(null);
    const [voiceModeData, setVoiceModeData] = useState<VoiceModeData | null>(null);
    const [customSimsCount, setCustomSimsCount] = useState({ total: 0, active: 0 });

    const usedSlots = currentOrg?.membersCount || 0;
    const maxSlots = currentOrg?.maxMembers || 5;
    const usagePercentage = Math.round((usedSlots / maxSlots) * 100);

    useEffect(() => {
        // Don't fetch if organization isn't loaded yet
        if (!currentOrg?.id) {
            return;
        }

        const fetchUsageData = async () => {
            try {
                setLoading(true);
                setError(null);
                const response = await apiGet<UsageResponse>('/organization/usage');

                if (response.status === 'success' && response.data) {
                    setUsageData(response.data.usage);
                    setAllTimeData(response.data.all_time);
                    setVoiceModeData(response.data.voice_mode);
                    setCustomSimsCount(response.data.custom_simulations || { total: 0, active: 0 });
                }
            } catch (err) {
                console.error('Failed to fetch usage data:', err);
                setError('Failed to load usage data');
            } finally {
                setLoading(false);
            }
        };

        fetchUsageData();
    }, [currentOrg?.id]);

    const formatDuration = (minutes: number): string => {
        if (minutes < 60) return `${Math.round(minutes)} min`;
        const hours = Math.floor(minutes / 60);
        const mins = Math.round(minutes % 60);
        return `${hours}h ${mins}m`;
    };

    const getUsageColor = () => {
        if (usagePercentage >= 100) return 'bg-red-500';
        if (usagePercentage >= 80) return 'bg-orange-500';
        return 'bg-emerald-500';
    };

    if (loading) {
        return (
            <div className="space-y-6">
                <div className="h-8 w-64 bg-slate-200 rounded animate-pulse" />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="h-64 bg-slate-200 rounded-xl animate-pulse" />
                    <div className="h-64 bg-slate-200 rounded-xl animate-pulse" />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="h-48 bg-slate-200 rounded-xl animate-pulse" />
                    <div className="h-48 bg-slate-200 rounded-xl animate-pulse" />
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div>
                <h2 className="text-xl font-semibold text-slate-900">Usage & Analytics</h2>
                <p className="text-slate-500 text-sm mt-1">
                    Monitor your organization's platform usage and statistics
                </p>
            </div>

            {error && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start space-x-3">
                    <AlertCircle className="w-5 h-5 text-red-600 mt-0.5" />
                    <div>
                        <p className="text-sm font-medium text-red-800">{error}</p>
                    </div>
                </div>
            )}

            {/* Main Usage Card */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
                <div className="px-6 py-4 border-b border-slate-100">
                    <h3 className="text-base font-semibold text-slate-900">Usage & Limits</h3>
                </div>
                <div className="p-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        {/* Seats Progress */}
                        <div className="flex flex-col items-center">
                            <div className="relative w-32 h-32">
                                <svg className="w-32 h-32 transform -rotate-90">
                                    <circle
                                        cx="64"
                                        cy="64"
                                        r="56"
                                        stroke="#e2e8f0"
                                        strokeWidth="12"
                                        fill="none"
                                    />
                                    <circle
                                        cx="64"
                                        cy="64"
                                        r="56"
                                        stroke={usagePercentage >= 100 ? '#ef4444' : usagePercentage >= 80 ? '#f97316' : '#10b981'}
                                        strokeWidth="12"
                                        fill="none"
                                        strokeDasharray={`${usagePercentage * 3.52} 352`}
                                        strokeLinecap="round"
                                    />
                                </svg>
                                <div className="absolute inset-0 flex flex-col items-center justify-center">
                                    <span className="text-3xl font-bold text-slate-900">{usedSlots}</span>
                                    <span className="text-sm text-slate-500">of {maxSlots}</span>
                                </div>
                            </div>
                            <div className="mt-4 text-center">
                                <p className="font-medium text-slate-900">Team Members</p>
                                <p className="text-sm text-slate-500">
                                    {maxSlots - usedSlots} seat{maxSlots - usedSlots !== 1 ? 's' : ''} available
                                </p>
                            </div>
                        </div>

                        {/* Stats List */}
                        <div className="space-y-4">
                            <div className="flex items-center space-x-4 p-3 rounded-lg hover:bg-slate-50 transition-colors">
                                <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center">
                                    <Users className="w-5 h-5 text-blue-500" />
                                </div>
                                <div className="flex-1">
                                    <p className="text-sm text-slate-500">Members</p>
                                    <p className="text-lg font-semibold text-slate-900">{usedSlots}</p>
                                    <p className="text-xs text-slate-400">Max {maxSlots} per org</p>
                                </div>
                            </div>

                            <div className="border-t border-slate-100" />

                            <div className="flex items-center space-x-4 p-3 rounded-lg hover:bg-slate-50 transition-colors">
                                <div className="w-10 h-10 bg-emerald-50 rounded-lg flex items-center justify-center">
                                    <Play className="w-5 h-5 text-emerald-500" />
                                </div>
                                <div className="flex-1">
                                    <p className="text-sm text-slate-500">Simulations Run</p>
                                    <p className="text-lg font-semibold text-slate-900">{usageData?.simulations_run || 0}</p>
                                    <p className="text-xs text-slate-400">This month</p>
                                </div>
                            </div>

                            <div className="border-t border-slate-100" />

                            <div className="flex items-center space-x-4 p-3 rounded-lg hover:bg-slate-50 transition-colors">
                                <div className="w-10 h-10 bg-purple-50 rounded-lg flex items-center justify-center">
                                    <FileText className="w-5 h-5 text-purple-500" />
                                </div>
                                <div className="flex-1">
                                    <p className="text-sm text-slate-500">Candidates Evaluated</p>
                                    <p className="text-lg font-semibold text-slate-900">{usageData?.candidates_evaluated || 0}</p>
                                    <p className="text-xs text-slate-400">Total</p>
                                </div>
                            </div>

                            <div className="border-t border-slate-100" />

                            <div className="flex items-center space-x-4 p-3 rounded-lg hover:bg-slate-50 transition-colors">
                                <div className="w-10 h-10 bg-orange-50 rounded-lg flex items-center justify-center">
                                    <Layers className="w-5 h-5 text-orange-500" />
                                </div>
                                <div className="flex-1">
                                    <p className="text-sm text-slate-500">Custom Simulations</p>
                                    <p className="text-lg font-semibold text-slate-900">{customSimsCount.active}</p>
                                    <p className="text-xs text-slate-400">Active ({customSimsCount.total} total)</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {usagePercentage >= 80 && (
                        <div className={`mt-6 p-4 rounded-lg ${
                            usagePercentage >= 100 
                                ? 'bg-red-50 border border-red-200' 
                                : 'bg-orange-50 border border-orange-200'
                        }`}>
                            <p className={`text-sm font-medium ${
                                usagePercentage >= 100 ? 'text-red-700' : 'text-orange-700'
                            }`}>
                                {usagePercentage >= 100
                                    ? 'Your organization has reached its member limit. Remove a member to invite new ones.'
                                    : 'Your organization is approaching its member limit.'}
                            </p>
                        </div>
                    )}
                </div>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* All-Time Statistics */}
                <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
                    <div className="px-6 py-4 border-b border-slate-100 flex items-center space-x-2">
                        <TrendingUp className="w-5 h-5 text-emerald-500" />
                        <h3 className="text-base font-semibold text-slate-900">All-Time Statistics</h3>
                    </div>
                    <div className="p-6 space-y-4">
                        <div className="flex justify-between items-center">
                            <span className="text-slate-600">Total Simulations</span>
                            <span className="font-semibold text-slate-900">{allTimeData?.total_simulations || 0}</span>
                        </div>
                        <div className="flex justify-between items-center">
                            <span className="text-slate-600">Completed</span>
                            <span className="font-semibold text-slate-900">{allTimeData?.completed_simulations || 0}</span>
                        </div>
                        <div className="flex justify-between items-center">
                            <span className="text-slate-600">Unique Users</span>
                            <span className="font-semibold text-slate-900">{allTimeData?.unique_users || 0}</span>
                        </div>
                        <div className="border-t border-slate-100 pt-4">
                            <div className="flex justify-between items-center">
                                <span className="text-slate-600">Avg Session Duration</span>
                                <span className="font-semibold text-slate-900">
                                    {formatDuration(allTimeData?.avg_session_duration_minutes || 0)}
                                </span>
                            </div>
                        </div>
                        <div className="flex justify-between items-center">
                            <span className="text-slate-600">Total Time Used</span>
                            <span className="font-semibold text-slate-900">
                                {formatDuration(allTimeData?.total_duration_minutes || 0)}
                            </span>
                        </div>
                    </div>
                </div>

                {/* Voice Mode Usage */}
                <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
                    <div className="px-6 py-4 border-b border-slate-100 flex items-center space-x-2">
                        <Mic className="w-5 h-5 text-purple-500" />
                        <h3 className="text-base font-semibold text-slate-900">Voice Mode Usage</h3>
                    </div>
                    <div className="p-6 space-y-4">
                        <div className="flex justify-between items-center">
                            <span className="text-slate-600">Voice Simulations</span>
                            <div className="flex items-center space-x-2">
                                <span className="font-semibold text-slate-900">{voiceModeData?.total_voice_simulations || 0}</span>
                                <span className="px-2 py-0.5 text-xs font-medium bg-purple-100 text-purple-700 rounded-full">
                                    {voiceModeData?.voice_usage_percentage || 0}%
                                </span>
                            </div>
                        </div>
                        <div className="flex justify-between items-center">
                            <span className="text-slate-600">Total Voice Time</span>
                            <span className="font-semibold text-slate-900">
                                {formatDuration(voiceModeData?.total_voice_minutes || 0)}
                            </span>
                        </div>
                        <div className="border-t border-slate-100 pt-4">
                            <div className="flex justify-between items-center">
                                <span className="text-slate-600">This Month Voice Sims</span>
                                <span className="font-semibold text-slate-900">{usageData?.voice_mode_simulations || 0}</span>
                            </div>
                        </div>
                        <div className="flex justify-between items-center">
                            <span className="text-slate-600">This Month Voice Time</span>
                            <span className="font-semibold text-slate-900">
                                {formatDuration(usageData?.total_voice_minutes || 0)}
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default OrganizationSettingsUsage;
